import { useState, useEffect, useCallback } from 'react';
import {
  Crown, Check, X, Heart, Smartphone, Palette, Languages,
  Wallet, PackageCheck, Database, Warehouse, ShoppingCart,
  BarChart2, MessageSquare, Folder, UserCheck, Mail, Shield,
  Star, Zap, Package, Briefcase, Loader2, AlertCircle,
  Users, Plus, Pencil, Trash2, ChevronDown, ChevronUp, Save,
  ToggleLeft, ToggleRight, RefreshCw
} from 'lucide-react';
import subscriptionController from '../controllers/subscriptionController';
import PaymentGatewayModal from '../components/PaymentGatewayModal'; // 🆕 apne actual path se adjust kar lena

// ─── Constants ───────────────────────────────────────────────────────────────
const DEFAULT_SECTIONS = ['dashboard', 'help', 'settings', 'subscriptions'];

const PLAN_ICONS = {
  hospital: Heart, crm: Users, hrms: UserCheck, projects: Folder,
  'mobile-device': Smartphone, 'white-label': Palette, translation: Languages,
  wallet: Wallet, orders: PackageCheck, inventory: Warehouse, sales: ShoppingCart,
  reports: BarChart2, enquiry: Mail, mdm: Database, communication: MessageSquare,
  healthcare_bundle: Heart, business_bundle: Briefcase, operations_bundle: Package,
  hr_bundle: UserCheck, tech_bundle: Smartphone, enterprise: Crown,
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
const Spinner = ({ size = 16 }) => (
  <Loader2 size={size} style={{ display:'inline', animation:'spin 1s linear infinite' }} />
);

const Banner = ({ type, message, onClose }) => {
  const colors = type === 'error'
    ? { bg:'#fef2f2', border:'#fca5a5', color:'#b91c1c' }
    : { bg:'#f0fdf4', border:'#86efac', color:'#15803d' };
  return (
    <div style={{ background: colors.bg, border:`1px solid ${colors.border}`, borderRadius:8, padding:'10px 14px', marginBottom:14, display:'flex', alignItems:'center', gap:8, color: colors.color, fontSize:13 }}>
      {type === 'error' ? <AlertCircle size={15}/> : <Check size={15}/>}
      <span style={{ flex:1 }}>{message}</span>
      <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color: colors.color, padding:0, display:'flex' }}><X size={13}/></button>
    </div>
  );
};

// ─── Feature list with expand/collapse ───────────────────────────────────────
const FeatureList = ({ features = [] }) => {
  const [open, setOpen] = useState(false);
  const shown = open ? features : features.slice(0, 5);
  return (
    <div style={{ marginBottom:12 }}>
      {shown.map((f, i) => (
        <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:6, padding:'3px 0', fontSize:12, color:'#475569', lineHeight:1.4 }}>
          <Check size={12} color="#10b981" style={{ flexShrink:0, marginTop:1 }}/><span>{f}</span>
        </div>
      ))}
      {features.length > 5 && (
        <button onClick={() => setOpen(v => !v)}
          style={{ background:'none', border:'none', color:'#3b82f6', fontSize:11, fontWeight:600, cursor:'pointer', padding:'3px 0', display:'flex', alignItems:'center', gap:3 }}>
          {open ? <><ChevronUp size={11}/>Show less</> : <><ChevronDown size={11}/>+{features.length-5} more</>}
        </button>
      )}
    </div>
  );
};

// ─── Plan Create / Edit Modal ─────────────────────────────────────────────────
const PlanModal = ({ plan, onSave, onClose, isSaving }) => {
  const isNew = !plan?.planId;
  const [form, setForm] = useState({
    planId:      plan?.planId      || '',
    name:        plan?.name        || '',
    category:    plan?.category    || '',
    description: plan?.description || '',
    type:        plan?.type        || 'individual',
    popular:     plan?.popular     || false,
    isActive:    plan?.isActive    !== false,
    badge:       plan?.badge       || '',
    savings:     plan?.savings     || '',
    features:    (plan?.features   || []).join('\n'),
    includes:    (plan?.includes   || []).join(', '),
    pricing_monthly:          plan?.pricing?.monthly          || 0,
    pricing_quarterly:        plan?.pricing?.quarterly        || 0,
    pricing_yearly:           plan?.pricing?.yearly           || 0,
    originalPrice_monthly:    plan?.originalPrice?.monthly    || 0,
    originalPrice_quarterly:  plan?.originalPrice?.quarterly  || 0,
    originalPrice_yearly:     plan?.originalPrice?.yearly     || 0,
  });

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = () => {
    if (!form.planId.trim() || !form.name.trim()) return;
    onSave({
      planId:      form.planId.trim().toLowerCase().replace(/\s+/g, '-'),
      name:        form.name.trim(),
      category:    form.category.trim(),
      description: form.description.trim(),
      type:        form.type,
      popular:     form.popular,
      isActive:    form.isActive,
      badge:       form.badge.trim(),
      savings:     form.savings.trim(),
      features:    form.features.split('\n').map(x => x.trim()).filter(Boolean),
      includes:    form.includes.split(',').map(x => x.trim().toLowerCase()).filter(Boolean),
      pricing:      { monthly: +form.pricing_monthly, quarterly: +form.pricing_quarterly, yearly: +form.pricing_yearly },
      originalPrice:{ monthly: +form.originalPrice_monthly, quarterly: +form.originalPrice_quarterly, yearly: +form.originalPrice_yearly },
    });
  };

  const showBundle = form.type === 'bundle' || form.type === 'enterprise';

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }}>
      <div style={{ background:'#fff', borderRadius:14, width:'100%', maxWidth:660, maxHeight:'92vh', display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 20px', borderBottom:'1px solid #f1f5f9' }}>
          <div>
            <h2 style={{ fontSize:16, fontWeight:700, color:'#1e293b', margin:0 }}>
              {isNew ? '✦ Create New Plan' : `Edit Plan — ${plan.name}`}
            </h2>
            <p style={{ fontSize:11, color:'#94a3b8', margin:'2px 0 0' }}>
              {isNew ? 'All fields with * are required' : `planId: ${plan.planId}`}
            </p>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'1px solid #e2e8f0', borderRadius:6, width:30, height:30, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'#64748b' }}>
            <X size={16}/>
          </button>
        </div>

        <div style={{ padding:'16px 20px', overflowY:'auto', flex:1, display:'flex', flexDirection:'column', gap:12 }}>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            <div>
              <label style={lbl}>Plan ID *</label>
              <input style={{ ...inp, ...(isNew ? {} : { background:'#f8fafc', color:'#94a3b8' }) }}
                value={form.planId} disabled={!isNew}
                onChange={e => f('planId', e.target.value)} placeholder="e.g. my-plan"/>
              {!isNew && <span style={{ fontSize:10, color:'#94a3b8' }}>Cannot change after creation</span>}
            </div>
            <div>
              <label style={lbl}>Name *</label>
              <input style={inp} value={form.name} onChange={e => f('name', e.target.value)} placeholder="Display name"/>
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            <div>
              <label style={lbl}>Category *</label>
              <input style={inp} value={form.category} onChange={e => f('category', e.target.value)} placeholder="e.g. Sales, HR, IT"/>
            </div>
            <div>
              <label style={lbl}>Type *</label>
              <select style={inp} value={form.type} onChange={e => f('type', e.target.value)}>
                <option value="individual">Individual</option>
                <option value="bundle">Bundle</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
          </div>

          <div>
            <label style={lbl}>Description</label>
            <textarea style={{ ...inp, height:56, resize:'vertical' }}
              value={form.description} onChange={e => f('description', e.target.value)}
              placeholder="Short plan description"/>
          </div>

          <div style={{ background:'#f8fafc', borderRadius:8, padding:'10px 12px' }}>
            <label style={{ ...lbl, marginBottom:8, display:'block' }}>Pricing ($) *</label>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8 }}>
              {[['monthly','Monthly'],['quarterly','Quarterly'],['yearly','Yearly']].map(([k,label]) => (
                <div key={k}>
                  <label style={{ fontSize:10, color:'#94a3b8', display:'block', marginBottom:3 }}>{label}</label>
                  <input style={inp} type="number" min="0" value={form[`pricing_${k}`]} onChange={e => f(`pricing_${k}`, e.target.value)}/>
                </div>
              ))}
            </div>
          </div>

          {showBundle && (
            <div style={{ background:'#fff7ed', borderRadius:8, padding:'10px 12px' }}>
              <label style={{ ...lbl, marginBottom:8, display:'block' }}>Original Price — before discount ($)</label>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8 }}>
                {[['monthly','Monthly'],['quarterly','Quarterly'],['yearly','Yearly']].map(([k,label]) => (
                  <div key={k}>
                    <label style={{ fontSize:10, color:'#94a3b8', display:'block', marginBottom:3 }}>{label}</label>
                    <input style={inp} type="number" min="0" value={form[`originalPrice_${k}`]} onChange={e => f(`originalPrice_${k}`, e.target.value)}/>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            <div>
              <label style={lbl}>Badge Text</label>
              <input style={inp} value={form.badge} onChange={e => f('badge', e.target.value)} placeholder="e.g. Most Popular"/>
            </div>
            <div>
              <label style={lbl}>Savings Label</label>
              <input style={inp} value={form.savings} onChange={e => f('savings', e.target.value)} placeholder="e.g. 25%"/>
            </div>
          </div>

          {showBundle && (
            <div>
              <label style={lbl}>Included Plan IDs (comma-separated)</label>
              <input style={inp} value={form.includes} onChange={e => f('includes', e.target.value)}
                placeholder="crm, hrms, reports, communication"/>
              <span style={{ fontSize:10, color:'#94a3b8' }}>These are the individual planId slugs this bundle contains</span>
            </div>
          )}

          <div>
            <label style={lbl}>Features (one per line)</label>
            <textarea style={{ ...inp, height:100, resize:'vertical', lineHeight:1.6 }}
              value={form.features} onChange={e => f('features', e.target.value)}
              placeholder={'Lead Management\nContact Management\nDeal Pipeline'}/>
          </div>

          <div style={{ display:'flex', gap:20 }}>
            <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, cursor:'pointer', color:'#475569', userSelect:'none' }}>
              <input type="checkbox" checked={form.popular} onChange={e => f('popular', e.target.checked)}/>
              Mark as Popular
            </label>
            <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, cursor:'pointer', color:'#475569', userSelect:'none' }}>
              <input type="checkbox" checked={form.isActive} onChange={e => f('isActive', e.target.checked)}/>
              Active (visible to users)
            </label>
          </div>
        </div>

        <div style={{ padding:'12px 20px', borderTop:'1px solid #f1f5f9', display:'flex', justifyContent:'flex-end', gap:8, background:'#fafafa' }}>
          <button onClick={onClose}
            style={{ padding:'8px 16px', background:'transparent', border:'1px solid #e2e8f0', borderRadius:7, fontSize:13, fontWeight:600, cursor:'pointer', color:'#475569' }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={isSaving || !form.planId.trim() || !form.name.trim()}
            style={{ padding:'8px 18px', background: (isSaving || !form.planId.trim() || !form.name.trim()) ? '#94a3b8' : '#1e293b', color:'#fff', border:'none', borderRadius:7, fontSize:13, fontWeight:600, cursor: isSaving ? 'wait' : 'pointer', display:'flex', alignItems:'center', gap:6 }}>
            {isSaving ? <><Spinner size={13}/> Saving…</> : <><Save size={13}/>{isNew ? 'Create Plan' : 'Save Changes'}</>}
          </button>
        </div>
      </div>
    </div>
  );
};

const lbl = { fontSize:11, fontWeight:600, color:'#475569', textTransform:'uppercase', letterSpacing:'0.4px', display:'block', marginBottom:4 };
const inp = { padding:'8px 10px', border:'1px solid #e2e8f0', borderRadius:7, fontSize:13, color:'#1e293b', outline:'none', background:'#fff', width:'100%', boxSizing:'border-box' };

// ─── Delete Confirm Modal ─────────────────────────────────────────────────────
const ConfirmModal = ({ planName, onConfirm, onCancel, isDeleting }) => (
  <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1010 }}>
    <div style={{ background:'#fff', borderRadius:12, padding:24, maxWidth:380, width:'90%', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
        <div style={{ width:36, height:36, borderRadius:'50%', background:'#fef2f2', display:'flex', alignItems:'center', justifyContent:'center' }}>
          <Trash2 size={16} color="#ef4444"/>
        </div>
        <h3 style={{ fontSize:15, fontWeight:700, color:'#1e293b', margin:0 }}>Deactivate Plan</h3>
      </div>
      <p style={{ fontSize:13, color:'#64748b', margin:'0 0 16px', lineHeight:1.5 }}>
        Are you sure you want to deactivate <strong>"{planName}"</strong>?
        Existing subscribers won't lose access immediately, but the plan will be hidden from new users.
      </p>
      <div style={{ display:'flex', justifyContent:'flex-end', gap:8 }}>
        <button onClick={onCancel}
          style={{ padding:'7px 14px', background:'transparent', border:'1px solid #e2e8f0', borderRadius:7, fontSize:13, cursor:'pointer', color:'#475569' }}>
          Cancel
        </button>
        <button onClick={onConfirm} disabled={isDeleting}
          style={{ padding:'7px 16px', background:'#ef4444', color:'#fff', border:'none', borderRadius:7, fontSize:13, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', gap:5 }}>
          {isDeleting ? <><Spinner size={12}/>Deactivating…</> : <><Trash2 size={12}/>Deactivate</>}
        </button>
      </div>
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const SubscriptionManagement = () => {
  const [user,               setUser]               = useState(null);
  const [subscribedSections, setSubscribedSections] = useState([]);
  const [individualPlans,    setIndividualPlans]     = useState([]);
  const [bundlePlans,        setBundlePlans]         = useState([]);
  const [enterprisePlan,     setEnterprisePlan]      = useState(null);
  const [selectedCycle,      setSelectedCycle]       = useState('monthly');
  const [activeTab,          setActiveTab]           = useState('individual');
  const [loading,            setLoading]             = useState(true);
  const [refreshing,         setRefreshing]          = useState(false);
  const [actionLoading,      setActionLoading]       = useState({});
  const [banner,             setBanner]              = useState(null);
  const [modalPlan,          setModalPlan]           = useState(null);
  const [confirmDelete,      setConfirmDelete]       = useState(null);
  const [isSaving,           setIsSaving]            = useState(false);
  const [isDeleting,         setIsDeleting]          = useState(false);
  const [paymentModal,       setPaymentModal]        = useState(null); // 🆕

  const currentUser  = user;
  const isSuperAdmin = subscriptionController.isSuperAdmin();
  const isAdminRole  = subscriptionController.isAdmin();
  const userId       = currentUser?.id || currentUser?._id;

  const notify = useCallback((type, message) => {
    setBanner({ type, message });
    setTimeout(() => setBanner(null), 4000);
  }, []);

  const loadAll = useCallback(async (uid, silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);

    try {
      const [plansRes, bundlesRes, enterpriseRes, subsRes] = await Promise.all([
        subscriptionController.getAvailablePlans(),
        subscriptionController.getBundlePlans(),
        subscriptionController.getEnterprisePlan(),
        uid ? subscriptionController.getSubscribedSections(uid) : Promise.resolve({ success: true, data: DEFAULT_SECTIONS }),
      ]);

      setIndividualPlans(plansRes.success    ? plansRes.data    : []);
      setBundlePlans(bundlesRes.success      ? bundlesRes.data  : []);
      setEnterprisePlan(enterpriseRes.success ? enterpriseRes.data : null);
      setSubscribedSections(subsRes.success   ? subsRes.data     : DEFAULT_SECTIONS);
    } catch (err) {
      console.error('loadAll error:', err);
      notify('error', 'Failed to load plans. Please check your connection.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [notify]);

  useEffect(() => {
    const u = subscriptionController.getCurrentUser();
    setUser(u);
    const uid = u?.id || u?._id;
    if (uid) {
      loadAll(uid, false);
    } else {
      setLoading(false);
    }
  }, [loadAll]);

  const setAct = (key, val) => setActionLoading(p => ({ ...p, [key]: val }));

  // 🆕 Actual subscribe — payment success ke baad hi call hota hai, invoiceId ke saath
  const doSubscribe = async (sectionIds, actionKey, invoiceId) => {
    const ids = Array.isArray(sectionIds) ? sectionIds : [sectionIds];
    setAct(actionKey, true);
    const res = ids.length > 1
      ? await subscriptionController.bulkSubscribe(userId, ids, selectedCycle, invoiceId)
      : await subscriptionController.subscribe(userId, ids[0], selectedCycle, invoiceId);
    if (res.success) {
      setSubscribedSections(prev => [...new Set([...prev, ...ids])]);
      notify('success', res.message || 'Subscribed successfully');
      window.dispatchEvent(new Event('subscriptions:updated'));
    } else {
      notify('error', res.message || 'Subscribe failed — payment verify nahi ho paya');
    }
    setAct(actionKey, false);
  };

  // 🆕 Button click pe ye call hota hai — pehle payment modal kholta hai
  const handleSubscribe = (sectionIds, actionKey, planNames, amount) => {
    setPaymentModal({
      sectionIds: Array.isArray(sectionIds) ? sectionIds : [sectionIds],
      actionKey,
      planNames: Array.isArray(planNames) ? planNames : [planNames],
      amount,
      billingCycle: selectedCycle,
    });
  };

  // 🆕 Payment modal se success aane par ye trigger hota hai (invoiceId ke saath)
  const handlePaymentSuccess = (invoiceId) => {
    if (!paymentModal) return;
    doSubscribe(paymentModal.sectionIds, paymentModal.actionKey, invoiceId);
    setPaymentModal(null);
  };

  const handleUnsubscribe = async (sectionIds, actionKey) => {
    const ids = Array.isArray(sectionIds) ? sectionIds : [sectionIds];
    setAct(actionKey, true);
    const res = ids.length > 1
      ? await subscriptionController.bulkUnsubscribe(userId, ids)
      : await subscriptionController.unsubscribe(userId, ids[0]);
    if (res.success) {
      setSubscribedSections(prev => prev.filter(id => !ids.includes(id)));
      notify('success', res.message || 'Unsubscribed successfully');
      window.dispatchEvent(new Event('subscriptions:updated'));
    } else {
      notify('error', res.message || 'Unsubscribe failed');
    }
    setAct(actionKey, false);
  };

  const handleSavePlan = async (formData) => {
    setIsSaving(true);
    const isNew = !modalPlan?.planId;
    const res = isNew
      ? await subscriptionController.createPlan(formData)
      : await subscriptionController.updatePlan(modalPlan.planId, formData);
    setIsSaving(false);
    if (res.success) {
      notify('success', res.message || (isNew ? 'Plan created!' : 'Plan updated!'));
      setModalPlan(null);
      loadAll(userId, true);
    } else {
      notify('error', res.message || 'Save failed');
    }
  };

  const handleToggleActive = async (plan) => {
    setAct(`toggle_${plan.planId}`, true);
    const res = await subscriptionController.updatePlan(plan.planId, { isActive: !plan.isActive });
    if (res.success) {
      notify('success', `Plan ${!plan.isActive ? 'activated' : 'deactivated'}`);
      loadAll(userId, true);
    } else {
      notify('error', res.message || 'Toggle failed');
    }
    setAct(`toggle_${plan.planId}`, false);
  };

  const handleDeleteConfirmed = async () => {
    if (!confirmDelete) return;
    setIsDeleting(true);
    const res = await subscriptionController.deletePlan(confirmDelete.planId);
    setIsDeleting(false);
    if (res.success) {
      notify('success', res.message || 'Plan deactivated');
      setConfirmDelete(null);
      loadAll(userId, true);
    } else {
      notify('error', res.message || 'Deactivate failed');
    }
  };

  const isSubscribed     = useCallback((id) => subscribedSections.includes(id), [subscribedSections]);
  const isBundleSubbed   = useCallback((inc = []) => inc.length > 0 && inc.every(id => subscribedSections.includes(id)), [subscribedSections]);
  const isEntSubbed      = useCallback(() => individualPlans.length > 0 && individualPlans.every(p => subscribedSections.includes(p.planId)), [subscribedSections, individualPlans]);
  const nonDefaultSubs   = subscribedSections.filter(id => !DEFAULT_SECTIONS.includes(id));
  const calcTotal        = () => individualPlans.filter(p => isSubscribed(p.planId)).reduce((s, p) => s + (p.pricing?.[selectedCycle] || 0), 0);
  const cycleLabel       = (c) => c === 'monthly' ? 'mo' : c === 'quarterly' ? 'qtr' : 'yr';

  const AdminActions = ({ plan }) => {
    const toggling = actionLoading[`toggle_${plan.planId}`];
    return (
      <div style={{ display:'flex', gap:4, alignItems:'center' }}>
        <button title="Edit plan" onClick={() => setModalPlan(plan)}
          style={{ background:'#eff6ff', border:'none', borderRadius:6, width:28, height:28, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'#3b82f6' }}>
          <Pencil size={13}/>
        </button>
        <button title={plan.isActive ? 'Deactivate' : 'Activate'} onClick={() => handleToggleActive(plan)}
          disabled={toggling}
          style={{ background: plan.isActive ? '#f0fdf4' : '#fef2f2', border:'none', borderRadius:6, width:28, height:28, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color: plan.isActive ? '#16a34a' : '#dc2626' }}>
          {toggling ? <Spinner size={11}/> : plan.isActive ? <ToggleRight size={14}/> : <ToggleLeft size={14}/>}
        </button>
        <button title="Deactivate plan" onClick={() => setConfirmDelete(plan)}
          style={{ background:'#fef2f2', border:'none', borderRadius:6, width:28, height:28, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'#ef4444' }}>
          <Trash2 size={13}/>
        </button>
      </div>
    );
  };

  const StatusPill = ({ isActive }) => (
    <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 8px', borderRadius:10, fontSize:10, fontWeight:700, background: isActive ? '#dcfce7' : '#fef2f2', color: isActive ? '#16a34a' : '#dc2626' }}>
      <span style={{ width:5, height:5, borderRadius:'50%', background: isActive ? '#16a34a' : '#dc2626', display:'inline-block' }}/>
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );

  const IndividualCard = ({ plan }) => {
    const Icon   = PLAN_ICONS[plan.planId] || Package;
    const subbed = isSubscribed(plan.planId);
    const busy   = actionLoading[plan.planId];

    return (
      <div style={{ background:'#fff', borderRadius:12, padding:18, border: plan.popular ? '2px solid #3b82f6' : '1px solid #f1f5f9', boxShadow:'0 1px 4px rgba(0,0,0,.06)', position:'relative', opacity: (!plan.isActive && !isSuperAdmin) ? 0.5 : 1 }}>
        {plan.popular && (
          <div style={{ position:'absolute', top:-10, right:14, background:'#3b82f6', color:'#fff', padding:'2px 10px', borderRadius:10, fontSize:10, fontWeight:700 }}>
            <Star size={9} style={{ display:'inline', marginRight:2 }}/>POPULAR
          </div>
        )}

        {isSuperAdmin && (
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10, paddingBottom:8, borderBottom:'1px solid #f1f5f9' }}>
            <StatusPill isActive={plan.isActive}/>
            <AdminActions plan={plan}/>
          </div>
        )}

        {subbed && !isSuperAdmin && (
          <div style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 8px', background:'#dcfce7', color:'#16a34a', borderRadius:10, fontSize:11, fontWeight:600, marginBottom:8 }}>
            <Check size={11}/>Active
          </div>
        )}

        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
          <div style={{ width:38, height:38, borderRadius:9, background:'#eff6ff', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <Icon size={20} color="#3b82f6"/>
          </div>
          <div>
            <h3 style={{ fontSize:14, fontWeight:700, color:'#1e293b', margin:0 }}>{plan.name}</h3>
            <span style={{ fontSize:11, color:'#94a3b8' }}>{plan.category}</span>
          </div>
        </div>

        <p style={{ fontSize:12, color:'#64748b', marginBottom:10, lineHeight:1.5 }}>{plan.description}</p>

        {plan.badge && (
          <span style={{ display:'inline-block', background:'#fef3c7', color:'#92400e', padding:'2px 8px', borderRadius:8, fontSize:11, fontWeight:600, marginBottom:8 }}>
            {plan.badge}
          </span>
        )}

        <div style={{ display:'flex', alignItems:'baseline', gap:3, marginBottom:12 }}>
          <span style={{ fontSize:24, fontWeight:700, color:'#1e293b' }}>${plan.pricing?.[selectedCycle] ?? '—'}</span>
          <span style={{ fontSize:12, color:'#94a3b8' }}>/{cycleLabel(selectedCycle)}</span>
        </div>

        <FeatureList features={plan.features || []}/>

        {isSuperAdmin && plan.includes?.length > 0 && (
          <div style={{ fontSize:11, color:'#94a3b8', marginBottom:8 }}>
            <strong>Includes:</strong> {plan.includes.join(', ')}
          </div>
        )}

        {!isSuperAdmin && plan.isActive && (
          subbed ? (
            <button disabled={busy} onClick={() => handleUnsubscribe(plan.planId, plan.planId)}
              style={{ width:'100%', padding:'9px', borderRadius:7, border:'none', background:'#ef4444', color:'#fff', fontSize:12, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:5 }}>
              {busy ? <Spinner size={12}/> : <X size={12}/>} Unsubscribe
            </button>
          ) : (
            // 🆕 amount aur planName bhi pass ho raha hai
            <button disabled={busy} onClick={() => handleSubscribe(plan.planId, plan.planId, plan.name, plan.pricing?.[selectedCycle] || 0)}
              style={{ width:'100%', padding:'9px', borderRadius:7, border:'none', background:'#3b82f6', color:'#fff', fontSize:12, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:5 }}>
              {busy ? <Spinner size={12}/> : null} Subscribe Now
            </button>
          )
        )}

        {isSuperAdmin && (
          <div style={{ fontSize:11, color:'#94a3b8', textAlign:'center', padding:'6px 0 2px' }}>
            Super admin — use edit to manage
          </div>
        )}
      </div>
    );
  };

  const BundleCard = ({ bundle }) => {
    const Icon   = PLAN_ICONS[bundle.planId] || Package;
    const subbed = isBundleSubbed(bundle.includes || []);
    const busy   = actionLoading[bundle.planId];

    return (
      <div style={{ background:'#fff', borderRadius:12, padding:18, border: bundle.popular ? '2px solid #3b82f6' : '1px solid #f1f5f9', boxShadow:'0 1px 4px rgba(0,0,0,.06)', position:'relative', opacity: (!bundle.isActive && !isSuperAdmin) ? 0.5 : 1 }}>
        {bundle.popular && (
          <div style={{ position:'absolute', top:-10, right:14, background:'#3b82f6', color:'#fff', padding:'2px 10px', borderRadius:10, fontSize:10, fontWeight:700 }}>
            <Star size={9} style={{ display:'inline', marginRight:2 }}/>POPULAR
          </div>
        )}

        {isSuperAdmin && (
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10, paddingBottom:8, borderBottom:'1px solid #f1f5f9' }}>
            <StatusPill isActive={bundle.isActive}/>
            <AdminActions plan={bundle}/>
          </div>
        )}

        {bundle.badge && <span style={{ display:'inline-block', background:'#fef3c7', color:'#92400e', padding:'2px 8px', borderRadius:8, fontSize:11, fontWeight:600, marginBottom:8 }}>{bundle.badge}</span>}
        {subbed && !isSuperAdmin && <div style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 8px', background:'#dcfce7', color:'#16a34a', borderRadius:10, fontSize:11, fontWeight:600, marginBottom:8 }}><Check size={11}/>Active Bundle</div>}

        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
          <div style={{ width:38, height:38, borderRadius:9, background:'#eff6ff', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <Icon size={20} color="#3b82f6"/>
          </div>
          <h3 style={{ fontSize:14, fontWeight:700, color:'#1e293b', margin:0 }}>{bundle.name}</h3>
        </div>

        <p style={{ fontSize:12, color:'#64748b', marginBottom:10, lineHeight:1.5 }}>{bundle.description}</p>

        <div style={{ marginBottom:12 }}>
          {bundle.originalPrice?.[selectedCycle] > 0 && (
            <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:2 }}>
              <span style={{ fontSize:13, color:'#94a3b8', textDecoration:'line-through' }}>${bundle.originalPrice[selectedCycle]}</span>
              {bundle.savings && <span style={{ fontSize:11, color:'#10b981', fontWeight:600 }}>Save {bundle.savings}</span>}
            </div>
          )}
          <div style={{ display:'flex', alignItems:'baseline', gap:3 }}>
            <span style={{ fontSize:24, fontWeight:700, color:'#1e293b' }}>${bundle.pricing?.[selectedCycle] ?? '—'}</span>
            <span style={{ fontSize:12, color:'#94a3b8' }}>/{cycleLabel(selectedCycle)}</span>
          </div>
        </div>

        <div style={{ marginBottom:12 }}>
          <div style={{ fontSize:11, fontWeight:600, color:'#475569', marginBottom:6 }}>Includes:</div>
          <FeatureList features={bundle.features || bundle.includes || []}/>
        </div>

        {!isSuperAdmin && bundle.isActive && (
          subbed ? (
            <button disabled={busy} onClick={() => handleUnsubscribe(bundle.includes, bundle.planId)}
              style={{ width:'100%', padding:'9px', borderRadius:7, border:'none', background:'#ef4444', color:'#fff', fontSize:12, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:5 }}>
              {busy ? <Spinner size={12}/> : <X size={12}/>} Unsubscribe Bundle
            </button>
          ) : (
            // 🆕 amount aur planName bhi pass ho raha hai
            <button disabled={busy} onClick={() => handleSubscribe(bundle.includes, bundle.planId, bundle.name, bundle.pricing?.[selectedCycle] || 0)}
              style={{ width:'100%', padding:'9px', borderRadius:7, border:'none', background:'#3b82f6', color:'#fff', fontSize:12, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:5 }}>
              {busy ? <Spinner size={12}/> : <Zap size={12}/>} Subscribe Bundle
            </button>
          )
        )}

        {isSuperAdmin && (
          <div style={{ fontSize:11, color:'#94a3b8', textAlign:'center', padding:'6px 0 2px' }}>
            Includes planIds: {(bundle.includes||[]).join(', ')||'—'}
          </div>
        )}
      </div>
    );
  };

  const EnterpriseSection = () => {
    if (!enterprisePlan) return (
      <div style={{ textAlign:'center', padding:'50px 0', color:'#94a3b8' }}>
        <Crown size={40} color="#e2e8f0" style={{ marginBottom:12 }}/>
        <p style={{ fontSize:14 }}>Enterprise plan not found in database.</p>
        {isSuperAdmin && (
          <button onClick={() => setModalPlan({ type:'enterprise' })}
            style={{ marginTop:12, padding:'9px 20px', background:'#1e293b', color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:6 }}>
            <Plus size={14}/>Create Enterprise Plan
          </button>
        )}
      </div>
    );

    const allIds = individualPlans.map(p => p.planId);
    const subbed = isEntSubbed();
    const busy   = actionLoading['enterprise'];

    return (
      <div style={{ maxWidth:820, margin:'0 auto' }}>
        <div style={{ background:'#fff', borderRadius:14, padding:28, border:'2px solid #3b82f6', boxShadow:'0 4px 20px rgba(59,130,246,.1)', position:'relative' }}>
          <div style={{ position:'absolute', top:-11, right:18, background:'#3b82f6', color:'#fff', padding:'2px 14px', borderRadius:10, fontSize:10, fontWeight:700 }}>
            <Crown size={9} style={{ display:'inline', marginRight:3 }}/>BEST DEAL
          </div>

          {isSuperAdmin && (
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, paddingBottom:14, borderBottom:'1px solid #f1f5f9' }}>
              <StatusPill isActive={enterprisePlan.isActive}/>
              <AdminActions plan={enterprisePlan}/>
            </div>
          )}

          {enterprisePlan.badge && (
            <span style={{ display:'inline-block', background:'#fef3c7', color:'#92400e', padding:'2px 10px', borderRadius:8, fontSize:11, fontWeight:600, marginBottom:14 }}>
              {enterprisePlan.badge}
            </span>
          )}
          {subbed && !isSuperAdmin && (
            <div style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 10px', background:'#dcfce7', color:'#16a34a', borderRadius:10, fontSize:11, fontWeight:600, marginBottom:14 }}>
              <Check size={11}/>Enterprise Active
            </div>
          )}

          <div style={{ textAlign:'center', marginBottom:22 }}>
            <Crown size={48} color="#f59e0b" style={{ marginBottom:10 }}/>
            <h2 style={{ fontSize:22, fontWeight:700, color:'#1e293b', margin:'0 0 6px' }}>{enterprisePlan.name}</h2>
            <p style={{ fontSize:13, color:'#64748b', maxWidth:480, margin:'0 auto' }}>{enterprisePlan.description}</p>
          </div>

          <div style={{ textAlign:'center', marginBottom:22 }}>
            {enterprisePlan.originalPrice?.[selectedCycle] > 0 && (
              <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:8, marginBottom:4 }}>
                <span style={{ fontSize:15, color:'#94a3b8', textDecoration:'line-through' }}>${enterprisePlan.originalPrice[selectedCycle]}</span>
                {enterprisePlan.savings && <span style={{ fontSize:12, color:'#10b981', fontWeight:600 }}>Save {enterprisePlan.savings}</span>}
              </div>
            )}
            <div style={{ fontSize:44, fontWeight:700, color:'#1e293b', lineHeight:1 }}>
              ${enterprisePlan.pricing?.[selectedCycle]}
            </div>
            <p style={{ color:'#64748b', fontSize:12, marginTop:4 }}>
              per {selectedCycle === 'monthly' ? 'month' : selectedCycle === 'quarterly' ? 'quarter' : 'year'}
            </p>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24, marginBottom:22 }}>
            <div>
              <h4 style={{ fontSize:12, fontWeight:700, color:'#1e293b', marginBottom:10, textTransform:'uppercase', letterSpacing:'0.4px' }}>All Features</h4>
              <FeatureList features={enterprisePlan.features || []}/>
            </div>
            <div>
              <h4 style={{ fontSize:12, fontWeight:700, color:'#1e293b', marginBottom:10, textTransform:'uppercase', letterSpacing:'0.4px' }}>Included Modules</h4>
              {(enterprisePlan.includes || []).map((id, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:6, padding:'3px 0', fontSize:12, color:'#475569' }}>
                  <Star size={10} color="#f59e0b" style={{ flexShrink:0 }}/><span>{id}</span>
                </div>
              ))}
            </div>
          </div>

          {!isSuperAdmin && enterprisePlan.isActive && (
            subbed ? (
              <button disabled={busy} onClick={() => handleUnsubscribe(allIds, 'enterprise')}
                style={{ width:'100%', padding:'12px', borderRadius:8, border:'none', background:'#ef4444', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                {busy ? <Spinner size={14}/> : <X size={14}/>} Cancel Enterprise
              </button>
            ) : (
              // 🆕 amount aur planName bhi pass ho raha hai
              <button disabled={busy} onClick={() => handleSubscribe(allIds, 'enterprise', enterprisePlan.name, enterprisePlan.pricing?.[selectedCycle])}
                style={{ width:'100%', padding:'12px', borderRadius:8, border:'none', background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                {busy ? <Spinner size={14}/> : <Crown size={14}/>} Subscribe to Enterprise
              </button>
            )
          )}

          {isSuperAdmin && (
            <div style={{ textAlign:'center', fontSize:11, color:'#94a3b8', paddingTop:8 }}>
              Super admin has full access — use Edit to manage this plan
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'60vh', gap:12, color:'#64748b' }}>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      <Loader2 size={36} color="#3b82f6" style={{ animation:'spin 1s linear infinite' }}/>
      <p style={{ fontSize:14, margin:0 }}>Loading subscription plans…</p>
    </div>
  );

  return (
    <div style={{ padding:24, backgroundColor:'#f8fafc', minHeight:'100vh' }}>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
        <div>
          <h1 style={{ fontSize:24, fontWeight:700, color:'#1e293b', margin:'0 0 4px', display:'flex', alignItems:'center', gap:8 }}>
            <Crown size={24} color="#f59e0b"/>Subscription Management
          </h1>
          <p style={{ fontSize:13, color:'#64748b', margin:0 }}>
            {isSuperAdmin ? 'Manage all subscription plans — create, edit, activate/deactivate.' : 'Choose individual modules, bundles, or the enterprise plan.'}
          </p>
        </div>
        <button onClick={() => loadAll(userId, true)} disabled={refreshing}
          style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 14px', background:'#fff', border:'1px solid #e2e8f0', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer', color:'#475569' }}>
          <RefreshCw size={13} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }}/>
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {banner && <Banner type={banner.type} message={banner.message} onClose={() => setBanner(null)}/>}

      {isSuperAdmin && (
        <div style={{ background:'#fefce8', border:'1.5px solid #fde047', borderRadius:10, padding:'12px 16px', marginBottom:18, display:'flex', alignItems:'flex-start', gap:10 }}>
          <Crown size={18} color="#92400e"/>
          <div>
            <strong style={{ fontSize:13, color:'#713f12' }}>Super Admin Mode</strong>
            <p style={{ fontSize:12, color:'#92400e', margin:'2px 0 0' }}>
              You can create new plans, edit existing ones, toggle active/inactive status, and delete plans. Use the icons on each card.
            </p>
          </div>
        </div>
      )}
      {currentUser?.role === 'admin' && (
        <div style={{ background:'#eff6ff', border:'1.5px solid #93c5fd', borderRadius:10, padding:'12px 16px', marginBottom:18, display:'flex', alignItems:'flex-start', gap:10 }}>
          <Shield size={18} color="#1d4ed8"/>
          <div>
            <strong style={{ fontSize:13, color:'#1e40af' }}>Admin Account</strong>
            <p style={{ fontSize:12, color:'#1e40af', margin:'2px 0 0' }}>Subscribe to individual sections or bundles. Bundle plans offer greater savings!</p>
          </div>
        </div>
      )}

      <div style={{ display:'flex', gap:2, marginBottom:18, borderBottom:'2px solid #e2e8f0' }}>
        {[
          { id:'individual', label:'Individual Plans', Icon:Package,  count: individualPlans.length },
          { id:'bundles',    label:'Bundle Plans',     Icon:Star,     count: bundlePlans.length },
          { id:'enterprise', label:'Enterprise',       Icon:Crown,    count: enterprisePlan ? 1 : 0 },
        ].map(({ id, label, Icon, count }) => (
          <button key={id}
            style={{ padding:'9px 16px', background:'transparent', border:'none', borderBottom:`3px solid ${activeTab === id ? '#3b82f6' : 'transparent'}`, cursor:'pointer', fontSize:13, fontWeight:600, color: activeTab === id ? '#3b82f6' : '#64748b', marginBottom:-2, display:'flex', alignItems:'center', gap:6, transition:'all .15s' }}
            onClick={() => setActiveTab(id)}>
            <Icon size={14}/>{label}
            <span style={{ fontSize:11, background: activeTab === id ? '#dbeafe' : '#f1f5f9', color: activeTab === id ? '#1d4ed8' : '#64748b', padding:'1px 6px', borderRadius:6, fontWeight:700 }}>
              {count}
            </span>
          </button>
        ))}
      </div>

      <div style={{ display:'flex', gap:4, marginBottom:20, background:'#fff', padding:4, borderRadius:9, width:'fit-content', boxShadow:'0 1px 3px rgba(0,0,0,.07)' }}>
        {['monthly','quarterly','yearly'].map(c => (
          <button key={c}
            style={{ padding:'7px 14px', background: selectedCycle === c ? '#3b82f6' : 'transparent', color: selectedCycle === c ? '#fff' : '#475569', border:'none', borderRadius:7, cursor:'pointer', fontSize:12, fontWeight:500, transition:'all .15s' }}
            onClick={() => setSelectedCycle(c)}>
            {c === 'monthly' ? 'Monthly' : c === 'quarterly' ? 'Quarterly · −10%' : 'Yearly · −15%'}
          </button>
        ))}
      </div>

      {!isSuperAdmin && nonDefaultSubs.length > 0 && (
        <div style={{ background:'#fff', borderRadius:12, padding:'14px 18px', marginBottom:20, boxShadow:'0 1px 3px rgba(0,0,0,.07)' }}>
          <h3 style={{ fontSize:14, fontWeight:700, marginBottom:10, color:'#1e293b' }}>
            Your Subscriptions ({nonDefaultSubs.length})
          </h3>
          {individualPlans.filter(p => isSubscribed(p.planId)).map(p => (
            <div key={p.planId} style={{ display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom:'1px solid #f8fafc', fontSize:13 }}>
              <span>{p.name}</span>
              <span style={{ fontWeight:600 }}>${p.pricing?.[selectedCycle] ?? '—'}</span>
            </div>
          ))}
          <div style={{ display:'flex', justifyContent:'space-between', padding:'10px 0 0', fontSize:15, fontWeight:700 }}>
            <span>Total / {selectedCycle === 'monthly' ? 'month' : selectedCycle === 'quarterly' ? 'quarter' : 'year'}</span>
            <span style={{ color:'#3b82f6' }}>${calcTotal()}</span>
          </div>
        </div>
      )}

      {isSuperAdmin && activeTab !== 'enterprise' && (
        <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:14 }}>
          <button onClick={() => setModalPlan({ type: activeTab === 'bundles' ? 'bundle' : 'individual' })}
            style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 16px', background:'#1e293b', color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>
            <Plus size={15}/>Create {activeTab === 'bundles' ? 'Bundle' : 'Individual'} Plan
          </button>
        </div>
      )}

      {activeTab === 'individual' && (
        individualPlans.length === 0
          ? <div style={{ textAlign:'center', padding:'50px 0', color:'#94a3b8', fontSize:14 }}>
              No individual plans found. {isSuperAdmin ? 'Create one using the button above.' : 'Contact your administrator.'}
            </div>
          : <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(290px,1fr))', gap:16 }}>
              {individualPlans.map(plan => <IndividualCard key={plan.planId} plan={plan}/>)}
            </div>
      )}
      {activeTab === 'bundles' && (
        bundlePlans.length === 0
          ? <div style={{ textAlign:'center', padding:'50px 0', color:'#94a3b8', fontSize:14 }}>
              No bundle plans found. {isSuperAdmin ? 'Create one using the button above.' : 'Contact your administrator.'}
            </div>
          : <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(290px,1fr))', gap:16 }}>
              {bundlePlans.map(bundle => <BundleCard key={bundle.planId} bundle={bundle}/>)}
            </div>
      )}
      {activeTab === 'enterprise' && <EnterpriseSection/>}

      {modalPlan !== null && (
        <PlanModal
          plan={modalPlan?.planId ? modalPlan : { type: modalPlan?.type || 'individual' }}
          onSave={handleSavePlan}
          onClose={() => setModalPlan(null)}
          isSaving={isSaving}
        />
      )}

      {confirmDelete && (
        <ConfirmModal
          planName={confirmDelete.name}
          onConfirm={handleDeleteConfirmed}
          onCancel={() => setConfirmDelete(null)}
          isDeleting={isDeleting}
        />
      )}

      {/* 🆕 Payment Gateway Modal — jab tak payment success na ho, subscribe API call hi nahi hoti */}
      {paymentModal && (
        <PaymentGatewayModal
          isOpen={!!paymentModal}
          onClose={() => setPaymentModal(null)}
          planNames={paymentModal.planNames}
          amount={paymentModal.amount}
          billingCycle={paymentModal.billingCycle}
          notes={{ sectionIds: paymentModal.sectionIds.join(','), userId }}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  );
};

export default SubscriptionManagement;